import { useApiMutation } from "@gladis/api-client";
import type { InviteDto } from "@gladis/shared";
import { useState, type ReactNode } from "react";
import { Button, Label, TextField } from "@gladis/ui";
import { formatDate, errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Invite someone to a household (FR-021 – FR-024).
 *
 * Co-located in the identity package rather than in `apps/app`, because it knows what an invite is.
 * `apps/app` composes it; it does not own it.
 *
 * THE LINK IS THE PRIMARY PATH, not a fallback. It is displayed as soon as the invite exists and
 * stays visible whether or not the email sent — a broken mail server must never be able to block
 * onboarding, and the person inviting can always just send the link themselves.
 */
export function InviteMemberForm({ householdId }: { householdId: string }): ReactNode {
  const t = useT();
  const [email, setEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const create = useApiMutation<InviteDto, { email: string }>(
    (client, input) => client.post<InviteDto>(`/households/${householdId}/invites`, input),
    { invalidates: [["invites", householdId]] },
  );

  const invite = create.data;

  async function copy(url: string): Promise<void> {
    // try/catch is permitted in a domain package's ui/ subpath: this is presentation, and a
    // clipboard permission denial is exactly what it is for. The ban applies to domain logic.
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard access can be refused. The link is on screen and selectable regardless, so
      // there is nothing to recover from — only a confirmation not to show.
      setCopied(false);
    }
  }

  if (invite) {
    return (
      <section className="rounded-control border border-line bg-panel p-5">
        <h2 className="text-xl font-semibold">{t("identity.invite.created")}</h2>

        {invite.emailDelivery === "failed" ? (
          // Non-blocking, and deliberately not an error: the invite is real and valid for seven
          // days. Only the delivery failed.
          <p className="mt-3 text-muted" role="status">
            {t("identity.invite.mailFailed")}
          </p>
        ) : (
          <p className="mt-3 text-muted" role="status">
            We&apos;ve emailed {invite.email}. You can also share this link.
          </p>
        )}

        <p className="mt-4">
          <code
            className="block overflow-x-auto rounded-control border border-line px-3 py-2 font-mono text-sm"
            data-testid="invite-url"
          >
            {invite.inviteUrl}
          </code>
        </p>
        <Button className="mt-3" type="button" onClick={() => void copy(invite.inviteUrl)}>
          {copied ? t("common.copied") : t("common.copyLink")}
        </Button>

        <p className="mt-4 text-sm text-muted">
          This link expires on {formatDate(invite.expiresAt)}.
        </p>

        <Button className="mt-4" type="button" onClick={() => create.reset()}>
          {t("identity.invite.another")}
        </Button>
      </section>
    );
  }

  return (
    <section className="rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.invite.heading")}</h2>
      <form
        className="mt-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({ email: email.trim() });
        }}
      >
        <Label htmlFor="invite-email">{t("identity.invite.theirEmail")}</Label>
        <TextField
          id="invite-email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button
          className="mt-4"
          type="submit"
          disabled={create.isPending || email.trim().length === 0}
        >
          {create.isPending ? t("common.sending") : t("identity.invite.send")}
        </Button>
      </form>

      {/* The API distinguishes "already a member" from "that's you", and each gets its own
          message — so the person reading it knows which mistake they made. */}
      {create.isError ? (
        <p className="mt-4 text-sm text-muted" role="alert">
          {errorSentence(create.error)}
        </p>
      ) : null}
    </section>
  );
}
